import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsUpDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { Badge } from '@/components/ui/badge';

import { useVendorsApi } from '../../../../common/hooks/use-vendors-api.hook';
import { Vendor } from '../../our-vendors/models/vendor.model';

/**
 * Every vendor a user can belong to: the customers, then the prospects the demo
 * generator made (the API lists them apart), one list sorted by name. Shared by
 * the invite and edit-user pickers. Prospects failing to load leaves the customers.
 */
export const loadPickerVendors = async (vendorsApi: ReturnType<typeof useVendorsApi>, page: number, limit: number): Promise<Vendor[]> => {
    const [customers, prospects] = await Promise.all([
        vendorsApi.findAllVendors(page, limit),
        vendorsApi.findAllVendors(page, limit, undefined, 'prospect').catch(() => ({ data: [] })),
    ]);
    return [
        ...(customers?.data ?? []),
        ...(prospects?.data ?? []).map((v: Vendor) => ({ ...v, kind: 'prospect' as const })),
    ].sort((a: Vendor, b: Vendor) => (a.name ?? '').localeCompare(b.name ?? ''));
};

const VendorLogo = ({ vendor }: { vendor?: Vendor }) =>
    vendor?.config?.logo ? (
        <img src={vendor.config.logo} alt="" className="size-5 shrink-0 rounded-sm object-contain" />
    ) : (
        <span className="size-5 shrink-0 rounded-sm border bg-muted" aria-hidden />
    );

export interface VendorComboboxProps {
    /** Put on the trigger so a `<Label htmlFor>` names it. */
    id?: string;
    vendors: Vendor[];
    /** The chosen vendor's _id. */
    value?: string;
    /** The chosen vendor as the user carries it — shown even if it is not in `vendors`. */
    selected?: Vendor;
    onChange: (vendorId: string) => void;
    invalid?: boolean;
    placeholder: string;
    searchPlaceholder: string;
    emptyText: string;
}

/**
 * Pick one vendor out of many: a searchable list (name, alias or id) in a
 * popover, the way the app's other pickers work. A plain Select stops being
 * usable past a couple of dozen entries.
 */
export function VendorCombobox({
    id,
    vendors,
    value,
    selected,
    onChange,
    invalid,
    placeholder,
    searchPlaceholder,
    emptyText,
}: VendorComboboxProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const current = vendors.find((v) => v._id === value) ?? (value && selected?._id === value ? selected : undefined);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    id={id}
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    aria-invalid={invalid || undefined}
                    className="w-full justify-between font-normal"
                    data-testid="vendor-combobox"
                >
                    {current ? (
                        <span className="flex min-w-0 items-center gap-2">
                            <VendorLogo vendor={current} />
                            <span className="truncate">{current.name}</span>
                        </span>
                    ) : (
                        <span className="text-muted-foreground">{placeholder}</span>
                    )}
                    <ChevronsUpDown className="opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-(--radix-popover-trigger-width) min-w-64 p-0">
                {/* cmdk names its input from the Command's label (aria-labelledby). */}
                <Command label={searchPlaceholder || t('ADMIN_SHELL.COMMON.SEARCH')}>
                    <CommandInput placeholder={searchPlaceholder} />
                    <CommandList>
                        <CommandEmpty>{emptyText}</CommandEmpty>
                        {vendors.map((vendor) => (
                            <CommandItem
                                key={vendor._id}
                                value={`${vendor.name ?? ''} ${vendor.alias ?? ''} ${vendor._id}`}
                                data-checked={vendor._id === value}
                                onSelect={() => {
                                    onChange(vendor._id);
                                    setOpen(false);
                                }}
                            >
                                <VendorLogo vendor={vendor} />
                                <span className="truncate">{vendor.name}</span>
                                {vendor.alias && (
                                    <span className="truncate text-xs text-muted-foreground">@{vendor.alias}</span>
                                )}
                                {vendor.kind === 'prospect' && (
                                    <Badge variant="outline" className="ml-auto shrink-0">
                                        {t('ADMIN_USERS.FORM.VENDOR_PROSPECT')}
                                    </Badge>
                                )}
                            </CommandItem>
                        ))}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
